"use client";

import { useEffect, useMemo, useState } from "react";
import { usePathname, useRouter } from "next/navigation";
import { buildAdvisorUrl } from "@/lib/advisor-context";
import { useBreakpoint } from "@/lib/hooks";
import type { AdvisorSurface } from "@/lib/advisor-context";
import type { AdvisorMode } from "@/lib/api";
import { HIDES_ASK_MAE_ATTR, askMaeBlocked } from "@/lib/ask-mae-float";

type MobileAdvisorTarget = {
  surface: AdvisorSurface;
  mode: AdvisorMode;
  prompt: string;
};

function getAdvisorTarget(pathname: string): MobileAdvisorTarget | null {
  if (pathname === "/") {
    return {
      surface: "home",
      mode: "refine_search",
      prompt: "Help me start or refine my home search.",
    };
  }

  if (pathname.startsWith("/searches")) {
    return {
      surface: "saved_search",
      mode: "watch_market",
      prompt: "Help me tune my saved searches and alerts.",
    };
  }

  if (pathname.startsWith("/profile")) {
    return {
      surface: "profile_memory",
      mode: "remember_preferences",
      prompt: "Help me review and clean up what Mae remembers.",
    };
  }

  if (pathname.startsWith("/favorites")) {
    return {
      surface: "profile_memory",
      mode: "remember_preferences",
      prompt: "Compare my saved homes and tell me what patterns matter.",
    };
  }

  return null;
}

export default function MobileAdvisorEntry() {
  const pathname = usePathname();
  const router = useRouter();
  const { isMobile } = useBreakpoint();

  const target = useMemo(() => getAdvisorTarget(pathname), [pathname]);
  // Hidden while anything the page marks data-hides-ask-mae is on screen (the home
  // hero and its own Ask Mae card), so it never covers the headline or search box.
  const [blocked, setBlocked] = useState(true);

  useEffect(() => {
    if (!isMobile || !target) return;
    let frame = 0;
    const check = () => {
      frame = 0;
      const boxes = Array.from(document.querySelectorAll(`[${HIDES_ASK_MAE_ATTR}]`), el => el.getBoundingClientRect());
      setBlocked(askMaeBlocked(boxes, window.innerHeight));
    };
    const schedule = () => { if (!frame) frame = window.requestAnimationFrame(check); };
    check();
    window.addEventListener("scroll", schedule, { passive: true });
    window.addEventListener("resize", schedule);
    return () => {
      window.removeEventListener("scroll", schedule);
      window.removeEventListener("resize", schedule);
      if (frame) window.cancelAnimationFrame(frame);
    };
  }, [isMobile, target, pathname]);

  if (!isMobile || !target || blocked) return null;
  if (
    pathname.startsWith("/assistant-profile")
    || pathname.startsWith("/results")
    || pathname.startsWith("/listing/")
    || pathname.startsWith("/auth")
    || pathname.startsWith("/onboarding")
  ) {
    return null;
  }

  return (
    <button
      type="button"
      onClick={() => router.push(buildAdvisorUrl(target))}
      aria-label="Ask Mae"
      style={{
        position: "fixed",
        right: "max(14px, env(safe-area-inset-right))",
        bottom: "calc(18px + env(safe-area-inset-bottom))",
        zIndex: 90,
        minHeight: 44,
        maxWidth: "calc(100vw - 28px - env(safe-area-inset-left) - env(safe-area-inset-right))",
        border: "1px solid var(--color-glass-dim)",
        borderRadius: "var(--radius-full)",
        background: "var(--color-brand-dark)",
        color: "var(--color-surface)",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 8,
        padding: "10px 15px",
        fontSize: 14,
        fontWeight: 700,
        cursor: "pointer",
        fontFamily: "inherit",
        boxShadow: "var(--shadow-menu)",
        WebkitTapHighlightColor: "transparent",
        whiteSpace: "nowrap",
      }}
    >
      <span aria-hidden="true" style={{ color: "var(--color-surface)", fontSize: 14, lineHeight: 1 }}>✦</span>
      Ask Mae
    </button>
  );
}
